import { useEffect, useRef, useState } from "react";
import { api, API, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import PreventivoCondizioni from "./PreventivoCondizioni";
import InstallerDistancesBox from "./InstallerDistancesBox";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Switch } from "./ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { CustomPaymentEditor, CUSTOM_PAY } from "./CustomPaymentEditor";
import { LayoutLockBox } from "./LayoutLockBox";
import { CfQuickInput } from "./CfQuickInput";
import { ListinoPicker } from "./ListinoPicker";
import { FileText, Loader2, Calculator, AlertTriangle, CheckCircle2, ArrowUpRight, TrendingUp, Plug, Landmark, FileSignature, FileDown, CreditCard, Percent, Lock, Unlock, Send, Users, Pencil, Plus, Trash2, ImagePlus, X, Paperclip, Eye, EyeOff, Download, Upload, Wrench, Check, Star } from "lucide-react";

const eur = (v) => (v === null || v === undefined ? "—" : Number(v).toLocaleString("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }));
const eur2 = (v) => (v === null || v === undefined ? "—" : Number(v).toLocaleString("it-IT", { style: "currency", currency: "EUR", minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const num = (v) => Number(v).toLocaleString("it-IT");

function ProposalAttachments({ lead, onUpdate, canEdit }) {
  const [uploading, setUploading] = useState(false);
  const [oggetto, setOggetto] = useState("");
  const [visible, setVisible] = useState(true);
  const [pendingFile, setPendingFile] = useState(null);
  const [busy, setBusy] = useState(null);
  const inputRef = useRef(null);
  const atts = lead.proposal_attachments || [];
  const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;
  const dl = (a) => `${API}/files/${a.storage_path}?auth=${token}`;
  const fmtSize = (b) => (!b ? "" : b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

  const doUpload = async () => {
    if (!pendingFile) { toast.error("Seleziona un file"); return; }
    if (!oggetto.trim()) { toast.error("Inserisci un oggetto per l'allegato"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", pendingFile);
      fd.append("oggetto", oggetto.trim());
      fd.append("visible_client", visible ? "true" : "false");
      const { data } = await api.post(`/leads/${lead.id}/proposal-attachment`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUpdate(data);
      setOggetto(""); setPendingFile(null); setVisible(true);
      if (inputRef.current) inputRef.current.value = "";
      toast.success("Allegato caricato");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const toggleVisible = async (a) => {
    setBusy(a.id);
    try {
      const { data } = await api.patch(`/leads/${lead.id}/proposal-attachment/${a.id}`, { visible_client: !a.visible_client });
      onUpdate(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const remove = async (a) => {
    setBusy(a.id);
    try {
      const { data } = await api.delete(`/leads/${lead.id}/proposal-attachment/${a.id}`);
      onUpdate(data);
      toast.success("Allegato rimosso");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  return (
    <Card className="rounded border-border p-5" data-testid="proposal-attachments-card">
      <h2 className="font-heading font-semibold mb-1 flex items-center gap-2"><Paperclip className="h-4 w-4 text-primary" strokeWidth={1.5} /> Allegati alla proposta</h2>
      <p className="text-xs text-muted-foreground mb-4">Carica documenti di riferimento (inviati dal cliente o a corredo) con un <b>oggetto</b>. Restano nell'archivio interno; quelli con <b>"Mostra al cliente"</b> attivo compaiono scaricabili nella proposta (Outlook e consulenza). PDF, immagini, Word, Excel · max 15 MB.</p>

      {atts.length > 0 && (
        <div className="space-y-2 mb-4" data-testid="proposal-attachments-list">
          {atts.map((a) => (
            <div key={a.id} className="flex items-center gap-3 rounded border border-border p-3" data-testid={`proposal-attachment-${a.id}`}>
              <FileText className="h-5 w-5 text-muted-foreground shrink-0" strokeWidth={1.5} />
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm truncate">{a.oggetto}</div>
                <div className="text-xs text-muted-foreground truncate">{a.original_filename}{a.size ? ` · ${fmtSize(a.size)}` : ""}</div>
              </div>
              <a href={dl(a)} target="_blank" rel="noreferrer" className="text-muted-foreground hover:text-primary shrink-0" title="Scarica" data-testid={`proposal-attachment-download-${a.id}`}><Download className="h-4 w-4" /></a>
              {canEdit && (
                <>
                  <button type="button" onClick={() => toggleVisible(a)} disabled={busy === a.id} title={a.visible_client ? "Visibile al cliente" : "Solo archivio interno"} className={`shrink-0 ${a.visible_client ? "text-primary" : "text-muted-foreground"}`} data-testid={`proposal-attachment-toggle-${a.id}`}>
                    {busy === a.id ? <Loader2 className="h-4 w-4 animate-spin" /> : (a.visible_client ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />)}
                  </button>
                  <button type="button" onClick={() => remove(a)} disabled={busy === a.id} title="Elimina" className="text-muted-foreground hover:text-destructive shrink-0" data-testid={`proposal-attachment-delete-${a.id}`}><Trash2 className="h-4 w-4" /></button>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {canEdit && (
        <div className="rounded border border-border bg-secondary/20 p-3 space-y-3" data-testid="proposal-attachment-uploader">
          <div className="space-y-1.5">
            <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Oggetto *</label>
            <Input value={oggetto} onChange={(e) => setOggetto(e.target.value)} placeholder="Es. Bolletta cliente, Planimetria, Visura…" data-testid="proposal-attachment-oggetto" />
          </div>
          <input ref={inputRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.gif,.doc,.docx,.xls,.xlsx" className="hidden" onChange={(e) => setPendingFile(e.target.files?.[0] || null)} data-testid="proposal-attachment-file" />
          <div className="flex flex-wrap items-center gap-3">
            <Button type="button" variant="outline" size="sm" onClick={() => inputRef.current?.click()} data-testid="proposal-attachment-choose-btn"><Upload className="h-4 w-4 mr-1.5" />{pendingFile ? "Cambia file" : "Scegli file"}</Button>
            {pendingFile && <span className="text-xs text-muted-foreground truncate max-w-[200px]">{pendingFile.name}</span>}
            <div className="flex items-center gap-2 ml-auto">
              <Switch checked={visible} onCheckedChange={setVisible} data-testid="proposal-attachment-visible-switch" />
              <span className="text-xs text-muted-foreground">Mostra al cliente</span>
            </div>
          </div>
          <Button size="sm" onClick={doUpload} disabled={uploading} className="font-semibold" data-testid="proposal-attachment-upload-btn">
            {uploading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Paperclip className="h-4 w-4 mr-1.5" />} Carica allegato
          </Button>
        </div>
      )}
      {!canEdit && atts.length === 0 && <p className="text-xs text-muted-foreground">Nessun allegato.</p>}
    </Card>
  );
}

function LayoutImages({ lead, onUpdate, canEdit }) {
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const inputRef = useRef(null);
  const images = lead.layout_images || [];
  const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;
  const imgUrl = (img) => `${API}/files/${img.storage_path}?auth=${token}`;

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      let latest = lead;
      for (const file of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post(`/leads/${lead.id}/layout-image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        latest = data;
      }
      onUpdate(latest);
      toast.success(files.length > 1 ? "Immagini caricate" : "Immagine caricata");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = async (img) => {
    setDeleting(img.id);
    try {
      const { data } = await api.delete(`/leads/${lead.id}/layout-image/${img.id}`);
      onUpdate(data);
      toast.success("Immagine rimossa");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setDeleting(null);
    }
  };

  return (
    <Card className="rounded border-border p-5" data-testid="layout-images-card">
      <h2 className="font-heading font-semibold mb-1 flex items-center gap-2"><ImagePlus className="h-4 w-4 text-primary" strokeWidth={1.5} /> Layout pannelli fotovoltaici</h2>
      <p className="text-xs text-muted-foreground mb-4">Carica una o più immagini del layout dei pannelli sul tetto del cliente. Compariranno nel documento per il cliente (Outlook) subito dopo l'analisi di fattibilità.</p>

      {images.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4" data-testid="layout-images-grid">
          {images.map((img) => (
            <div key={img.id} className="relative group rounded border border-border overflow-hidden" data-testid={`layout-image-${img.id}`}>
              <img src={imgUrl(img)} alt={img.original_filename || "Layout pannelli"} className="w-full h-32 object-cover" />
              {canEdit && (
                <button
                  type="button"
                  onClick={() => remove(img)}
                  disabled={deleting === img.id}
                  className="absolute top-1.5 right-1.5 h-7 w-7 rounded-full bg-background/90 border border-border flex items-center justify-center text-muted-foreground hover:text-destructive"
                  data-testid={`layout-image-delete-${img.id}`}
                >
                  {deleting === img.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <X className="h-4 w-4" />}
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {canEdit ? (
        <>
          <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" multiple className="hidden" onChange={(e) => handleFiles(e.target.files)} data-testid="layout-image-input" />
          <Button variant="outline" size="sm" onClick={() => inputRef.current?.click()} disabled={uploading} data-testid="layout-image-upload-btn">
            {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ImagePlus className="h-4 w-4 mr-2" />} {images.length > 0 ? "Aggiungi immagine" : "Carica immagine layout"}
          </Button>
        </>
      ) : (
        images.length === 0 && <p className="text-xs text-muted-foreground">Nessuna immagine di layout caricata.</p>
      )}
    </Card>
  );
}

function Stat({ label, value, testid, big }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className={`font-heading font-bold ${big ? "text-xl text-primary" : "text-base"}`} data-testid={testid}>{value}</div>
    </div>
  );
}

export default function LeadPreventivo({ lead, onUpdate, canEdit = true, reteEditor = false, listino = null }) {
  const [pickCod, setPickCod] = useState("");
  const isReteLive = lead.channel === "rete" && !lead.rete_unlocked;
  const reteSeller = isReteLive ? (lead.cost_estimate?.rete_partner?.installer || lead.rete_installer?.ragione_sociale) : "";
  const reteBrand = (t) => (reteSeller && t ? t.replace(/Fier\s+S\.p\.A\.(\s+a\s+socio\s+unico)?/gi, reteSeller).replace(/\bFIER\b/g, reteSeller) : t);
  const analysis = lead.analysis || {};
  const cost = lead.cost_estimate;
  const pvReady = !!analysis?.pv_kwp;
  const [dist, setDist] = useState(cost?.distanza_km ?? "");
  const [giorni, setGiorni] = useState(cost?.giorni ?? "");
  const [giorniAuto, setGiorniAuto] = useState(cost?.giorni_auto === false ? false : true);
  const [prezzo, setPrezzo] = useState(cost?.prezzo ?? "");
  const [ricarica, setRicarica] = useState(!!cost?.ricarica);
  const [ricaricaId, setRicaricaId] = useState(cost?.ricarica_id ?? "");
  const [ricariche, setRicariche] = useState([]);
  const [finanziamento, setFinanziamento] = useState(!!cost?.finanziamento?.attivo && !isReteLive);
  const [acconto, setAcconto] = useState(cost?.finanziamento?.acconto ?? "");
  const [durata, setDurata] = useState(String(cost?.finanziamento?.durata_mesi ?? 60));
  const [pagamentiList, setPagamentiList] = useState([]);
  const [pagamentoSel, setPagamentoSel] = useState(cost?.pagamento_titolo ?? "");
  const [bonusList, setBonusList] = useState([]);
  const [bonusSel, setBonusSel] = useState(cost?.bonus_nome ?? "");
  const [calc, setCalc] = useState(false);
  const [savingPrice, setSavingPrice] = useState(false);
  const [autoInfo, setAutoInfo] = useState(null);
  const [autoLoading, setAutoLoading] = useState(false);
  const [nPannelli, setNPannelli] = useState(cost?.componenti?.pannello?.n_pannelli ?? "");
  const [nBatterie, setNBatterie] = useState(cost?.componenti?.batteria?.n_batterie ?? "");
  const [applyingConfig, setApplyingConfig] = useState(false);
  const [cer, setCer] = useState(cost?.cer ?? false);
  const [colore, setColore] = useState(cost?.pannello_colore || "standard");
  const meterKw = Number(lead.analysis?.potenza_contatore_kw);
  const defaultContatore = Number.isFinite(meterKw) && meterKw > 0
    ? (meterKw <= 6 ? "monofase" : "trifase")
    : ((lead.target || "residenziale") === "residenziale" ? "monofase" : "trifase");
  const [contatore, setContatore] = useState(cost?.contatore || defaultContatore);
  const [colorePotenza, setColorePotenza] = useState(cost?.colore_potenza_wp ?? cost?.componenti?.pannello?.potenza_wp ?? "");
  const [coloriList, setColoriList] = useState([]);
  const [photoByModel, setPhotoByModel] = useState({});
  const [savingCer, setSavingCer] = useState(false);
  const [cerBenefit, setCerBenefit] = useState(null);
  const [calcCer, setCalcCer] = useState(false);
  const [editCosts, setEditCosts] = useState(false);
  const [sheetBusy, setSheetBusy] = useState(false);
  const downloadCostSheet = async () => {
    setSheetBusy(true);
    try {
      const { data } = await api.get(`/leads/${lead.id}/cost-sheet.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = `Scheda_costo_${(lead.name || "cliente").replace(/[^\w-]+/g, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch { toast.error("Download della scheda di costo non riuscito"); }
    finally { setSheetBusy(false); }
  };
  const [savingCosts, setSavingCosts] = useState(false);
  const [costOv, setCostOv] = useState(cost?.item_overrides || {});
  const [extras, setExtras] = useState(cost?.extra_items || []);
  const [manCfg, setManCfg] = useState(null);
  const [manAttiva, setManAttiva] = useState(!!cost?.manutenzione?.attiva);
  const [manAtt, setManAtt] = useState((cost?.manutenzione?.attivita || []).map((a) => a.key));
  const [manMod, setManMod] = useState(cost?.manutenzione?.modalita || "annuo");
  const [manPa, setManPa] = useState(cost?.manutenzione?.prezzo_annuo ?? "");
  const [manPk, setManPk] = useState(cost?.manutenzione?.prezzo_kwp ?? "");
  const [manIncluso, setManIncluso] = useState(!!cost?.manutenzione?.incluso);
  const [manAnni, setManAnni] = useState(cost?.manutenzione?.anni ?? 1);
  const [manGestione, setManGestione] = useState(cost?.manutenzione?.gestione || "diretta");
  const [savingMan, setSavingMan] = useState(false);
  const contract = lead.contract;
  const [prevBusy, setPrevBusy] = useState(false);
  const [renaming, setRenaming] = useState(null);
  const preventivi = lead.preventivi || [];
  const activeId = lead.active_preventivo_id;
  const activeLocked = !!((preventivi.find((p) => p.id === activeId) || {}).locked || cost?.locked);
  const editable = canEdit && !activeLocked;
  const activePrev = preventivi.find((p) => p.id === activeId) || {};
  const activePrevEvents = activePrev.events || [];
  const fmtDT = (x) => { try { return new Date(x).toLocaleString("it-IT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };
  const isProject = lead.level === 3;
  const isAdmin = ["admin", "team_leader"].includes((useAuth() || {}).user?.role);
  // Il Technical Sales (progetti · Livello 3) e gli admin possono bypassare la bolletta:
  // il preventivo si attiva comunque, con 0 pannelli e 0 batterie da configurare manualmente.
  const canBypassBill = isProject || isAdmin || reteEditor;
  const condizioniCanEdit = isProject && editable && !contract?.archived;
  const finLabel = isProject ? "Leasing / Noleggio operativo" : "Finanziamento Findomestic";
  const [leasingTasso, setLeasingTasso] = useState(5);
  const [unlocking, setUnlocking] = useState(false);
  const autoComputeRef = useRef(null);

  const unlockPrev = async () => {
    setUnlocking(true);
    try {
      const { data } = await api.post(`/leads/${lead.id}/preventivi/${activeId}/unlock`);
      onUpdate(data);
      toast.success("Preventivo sbloccato: ora puoi modificarlo");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setUnlocking(false);
    }
  };

  useEffect(() => {
    if (!editCosts) {
      setCostOv(cost?.item_overrides || {});
      setExtras(cost?.extra_items || []);
    }
  }, [cost?.item_overrides, cost?.extra_items, editCosts]);

  const ovVal = (voce, field, fallback) => {
    const o = costOv[voce];
    return o && o[field] !== undefined && o[field] !== null && o[field] !== "" ? o[field] : fallback;
  };
  const setOv = (voce, field, value) => setCostOv((p) => ({ ...p, [voce]: { ...(p[voce] || {}), [field]: value } }));
  const addExtra = () => setExtras((p) => [...p, { voce: "", quantita: 1, prezzo_unitario: 0, unita: "pz" }]);
  const setExtra = (i, field, value) => setExtras((p) => p.map((e, idx) => (idx === i ? { ...e, [field]: value } : e)));
  const removeExtra = (i) => setExtras((p) => p.filter((_, idx) => idx !== i));

  const saveCosts = async () => {
    setSavingCosts(true);
    try {
      const clean = {};
      Object.entries(costOv).forEach(([voce, o]) => {
        const q = o.quantita === "" || o.quantita === undefined ? null : Number(o.quantita);
        const pu = o.prezzo_unitario === "" || o.prezzo_unitario === undefined ? null : Number(o.prezzo_unitario);
        if (q !== null || pu !== null) clean[voce] = { quantita: q, prezzo_unitario: pu };
      });
      const ex = extras.filter((e) => (e.voce || "").trim()).map((e) => ({ voce: e.voce.trim(), quantita: Number(e.quantita) || 0, prezzo_unitario: Number(e.prezzo_unitario) || 0, unita: e.unita || "pz", ...(e.codice ? { codice: e.codice } : {}) }));
      const { data } = await api.post(`/leads/${lead.id}/cost-estimate`, {
        distanza_km: dist === "" ? 0 : Number(dist),
        giorni: giorni === "" ? 1 : Number(giorni),
        ricarica, ricarica_id: ricarica ? (ricaricaId || null) : null,
        finanziamento, acconto: acconto === "" ? 0 : Number(acconto), durata_mesi: Number(durata),
        pagamento: finanziamento ? null : (pagamentoSel || null), bonus: bonusSel || null,
        n_pannelli: nPannelli === "" ? null : Number(nPannelli),
        n_batterie: nBatterie === "" ? null : Number(nBatterie),
        cer,
        pannello_colore: colore,
        item_overrides: clean,
        extra_items: ex,
      });
      onUpdate(data);
      setPrezzo(data.cost_estimate?.prezzo ?? "");
      setEditCosts(false);
      toast.success("Costi aggiornati");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSavingCosts(false);
    }
  };

  useEffect(() => {
    setNPannelli(cost?.componenti?.pannello?.n_pannelli ?? "");
    setNBatterie(cost?.componenti?.batteria?.n_batterie ?? (cost ? 0 : ""));
  }, [cost, cost?.componenti?.pannello?.n_pannelli, cost?.componenti?.batteria?.n_batterie]);

  useEffect(() => { setCer(cost?.cer ?? false); }, [cost?.cer]);
  useEffect(() => { setColore(cost?.pannello_colore || "standard"); }, [cost?.pannello_colore]);
  useEffect(() => { setContatore(cost?.contatore || defaultContatore); }, [cost?.contatore]); // eslint-disable-line
  useEffect(() => { setColorePotenza(cost?.colore_potenza_wp ?? cost?.componenti?.pannello?.potenza_wp ?? ""); }, [cost?.colore_potenza_wp, cost?.componenti?.pannello?.potenza_wp]);
  useEffect(() => { api.get("/settings/colori-pannello").then((r) => setColoriList(r.data.list || [])).catch(() => {}); }, []);
  useEffect(() => {
    api.get("/settings/manutenzione").then((r) => {
      setManCfg(r.data);
      if (!cost?.manutenzione) {
        setManAttiva(!!r.data.attiva_default);
        setManAtt((r.data.attivita || []).filter((a) => a.active).map((a) => a.key));
        setManMod(r.data.modalita || "annuo");
      }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    const m = cost?.manutenzione;
    if (m) {
      setManAttiva(!!m.attiva);
      setManAtt((m.attivita || []).map((a) => a.key));
      setManMod(m.modalita || "annuo");
      setManIncluso(!!m.incluso);
      setManAnni(m.anni ?? 1);
      setManGestione(m.gestione || "diretta");
      if (m.prezzo_custom) { setManPa(m.prezzo_annuo ?? ""); setManPk(m.prezzo_kwp ?? ""); }
    }
  }, [cost?.manutenzione]);

  const saveManutenzione = async (over = {}) => {
    setSavingMan(true);
    try {
      const body = {
        distanza_km: dist === "" ? 0 : Number(dist),
        giorni: giorni === "" ? 1 : Number(giorni),
        ricarica, ricarica_id: ricarica ? (ricaricaId || null) : null,
        finanziamento, acconto: acconto === "" ? 0 : Number(acconto), durata_mesi: Number(durata),
        pagamento: finanziamento ? null : (pagamentoSel || null), bonus: bonusSel || null,
        n_pannelli: nPannelli === "" ? null : Number(nPannelli),
        n_batterie: nBatterie === "" ? null : Number(nBatterie),
        cer, pannello_colore: colore,
        manutenzione_attiva: manAttiva,
        manutenzione_attivita: manAtt,
        manutenzione_modalita: manMod,
        manutenzione_prezzo_annuo: manPa === "" ? null : Number(manPa),
        manutenzione_prezzo_kwp: manPk === "" ? null : Number(manPk),
        manutenzione_incluso: manIncluso,
        manutenzione_anni: manAnni === "" ? 1 : Number(manAnni),
        manutenzione_gestione: manGestione,
        ...over,
      };
      const { data } = await api.post(`/leads/${lead.id}/cost-estimate`, body);
      onUpdate(data);
      toast.success("Manutenzione aggiornata");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingMan(false); }
  };
  useEffect(() => { api.get("/products").then((r) => { const m = {}; (r.data || []).forEach((p) => { if (p.official_photo_url || p.photo_url) m[p.model] = p.official_photo_url || p.photo_url; }); setPhotoByModel(m); }).catch(() => {}); }, []);

  const overrides = () => ({
    n_pannelli: nPannelli === "" ? null : Number(nPannelli),
    n_batterie: nBatterie === "" ? null : Number(nBatterie),
    cer,
    contatore,
  });

  const toggleCer = async (value) => {
    setCer(value);
    if (!value) setCerBenefit(null);
    if (!cost) return; // verrà applicato al calcolo del costo
    setSavingCer(true);
    try {
      const { data } = await api.post(`/leads/${lead.id}/cost-estimate`, {
        distanza_km: dist === "" ? 0 : Number(dist),
        giorni: giorni === "" ? 1 : Number(giorni),
        ricarica,
        ricarica_id: ricarica ? (ricaricaId || null) : null,
        finanziamento,
        acconto: acconto === "" ? 0 : Number(acconto),
        durata_mesi: Number(durata),
        pagamento: finanziamento ? null : (pagamentoSel || null),
        bonus: bonusSel || null,
        n_pannelli: nPannelli === "" ? null : Number(nPannelli),
        n_batterie: nBatterie === "" ? null : Number(nBatterie),
        cer: value,
      });
      onUpdate(data);
      setPrezzo(data.cost_estimate?.prezzo ?? "");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setCer(!value);
    } finally {
      setSavingCer(false);
    }
  };

  const computeCerBenefit = async () => {
    setCalcCer(true);
    try {
      if (!cer) await toggleCer(true);
      const { data } = await api.get(`/leads/${lead.id}/outlook`);
      const p = data.piano;
      if (p && p.cer_attivo) {
        setCerBenefit({ totale: p.cer_totale, annuo: p.cer_annuo_medio, anni: p.anni });
        toast.success("Beneficio CER calcolato");
      } else {
        setCerBenefit(null);
        toast.error("Calcola prima il costo commerciale e il PVGIS per stimare il beneficio");
      }
    } catch (e) {
      toast.error("Errore nel calcolo del beneficio");
    } finally {
      setCalcCer(false);
    }
  };

  const cliSafe = (lead.name || "cliente").replace(/[^A-Za-z0-9]+/g, "_");

  const fetchAuto = async (apply = true) => {
    setAutoLoading(true);
    try {
      const { data } = await api.get(`/leads/${lead.id}/transfer-estimate`);
      setAutoInfo(data);
      if (apply) {
        setDist(String(data.distanza_km));
        setGiorni(String(data.giorni));
      }
      return data;
    } catch (e) {
      if (apply) toast.error(formatApiError(e.response?.data?.detail));
      return null;
    } finally {
      setAutoLoading(false);
    }
  };

  useEffect(() => {
    // Pre-compila automaticamente distanza e giorni se non ancora impostati
    if ((cost?.distanza_km === undefined || cost?.distanza_km === null) && (dist === "" || dist === undefined)) {
      fetchAuto(true);
    } else {
      fetchAuto(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead.id]);

  useEffect(() => {
    api.get("/products").then((r) => {
      const list = r.data.filter((p) => p.category === "ricarica" && p.active !== false);
      setRicariche(list);
      if (!cost?.ricarica_id && list.length > 0) setRicaricaId((prev) => prev || list[0].id);
    }).catch(() => {});
    api.get("/settings/condizioni").then((r) => {
      const pl = (r.data.pagamenti || []).filter((p) => !p.findomestic && !p.leasing);
      setPagamentiList(pl);
      setLeasingTasso(r.data.leasing_tasso ?? 5);
      setPagamentoSel((prev) => prev || (cost?.pagamento_titolo) || (pl[0]?.titolo ?? ""));
      const bl = r.data.bonus || [];
      setBonusList(bl);
      setBonusSel((prev) => {
        if (prev) return prev;
        if (cost?.bonus_nome) return cost.bonus_nome;
        const isRes = (lead.target || "residenziale") === "residenziale";
        const match = isRes ? bl.find((b) => (b.percentuale || 0) > 0) : bl.find((b) => (b.percentuale || 0) === 0);
        return (match || bl[0])?.nome ?? "";
      });
    }).catch(() => {});
  }, []); // eslint-disable-line

  const compute = async (opts = {}) => {
    setCalc(true);
    try {
      const { data } = await api.post(`/leads/${lead.id}/cost-estimate`, {
        distanza_km: dist === "" ? 0 : Number(dist),
        giorni: opts.giorni != null ? opts.giorni : (giorni === "" ? 1 : Number(giorni)),
        giorni_auto: opts.giorniAuto != null ? opts.giorniAuto : giorniAuto,
        ricarica,
        ricarica_id: ricarica ? (ricaricaId || null) : null,
        finanziamento,
        acconto: acconto === "" ? 0 : Number(acconto),
        durata_mesi: Number(durata),
        ...overrides(),
        pagamento: finanziamento ? null : (pagamentoSel || null),
        bonus: bonusSel || null,
      });
      onUpdate(data);
      setPrezzo(data.cost_estimate?.prezzo ?? "");
      setGiorni(String(data.cost_estimate?.giorni ?? ""));
      setGiorniAuto(data.cost_estimate?.giorni_auto === false ? false : true);
      toast.success("Costo commerciale calcolato");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setCalc(false);
    }
  };

  // Calcola automaticamente il preventivo alla prima apertura della scheda
  // (una sola volta per lead) così giorni di cantiere e comunità energetica
  // sono subito disponibili senza dover premere "Calcola". Il CF non è richiesto qui.
  useEffect(() => {
    if ((pvReady || canBypassBill) && !cost && !calc && canEdit && autoComputeRef.current !== lead.id) {
      autoComputeRef.current = lead.id;
      compute();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pvReady, canBypassBill, cost, calc, canEdit, lead.id]);

  // Sincronizza il prezzo mostrato quando si cambia preventivo attivo
  useEffect(() => { setPrezzo(cost?.prezzo ?? ""); }, [lead.active_preventivo_id]); // eslint-disable-line

  const switchPrev = async (pid) => {
    if (pid === activeId || prevBusy) return;
    setPrevBusy(true);
    try {
      const { data } = await api.put(`/leads/${lead.id}/preventivi/${pid}/activate`);
      onUpdate(data);
      toast.success("Preventivo attivato");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setPrevBusy(false); }
  };
  const createPrev = async () => {
    setPrevBusy(true);
    try {
      const { data } = await api.post(`/leads/${lead.id}/preventivi`);
      onUpdate(data);
      toast.success("Nuovo preventivo creato");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setPrevBusy(false); }
  };
  const deletePrev = async (pid) => {
    setPrevBusy(true);
    try {
      const { data } = await api.delete(`/leads/${lead.id}/preventivi/${pid}`);
      onUpdate(data);
      toast.success("Preventivo eliminato");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setPrevBusy(false); }
  };
  const renamePrev = async (pid, label) => {
    setPrevBusy(true);
    try {
      const { data } = await api.put(`/leads/${lead.id}/preventivi/${pid}/label`, { label });
      onUpdate(data);
      setRenaming(null);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setPrevBusy(false); }
  };
  const toggleConsigliato = async (pid) => {
    setPrevBusy(true);
    try {
      const { data } = await api.put(`/leads/${lead.id}/preventivi/${pid}/consigliato`);
      onUpdate(data);
      toast.success("Preventivo consigliato aggiornato");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setPrevBusy(false); }
  };

  const toggleVisibility = async (pid) => {
    setPrevBusy(true);
    try {
      const { data } = await api.put(`/leads/${lead.id}/preventivi/${pid}/visibility`);
      onUpdate(data);
      toast.success("Visibilità preventivo aggiornata");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setPrevBusy(false); }
  };

  const savePagamento = async (val, pagamentoCustom) => {
    const prevSel = pagamentoSel;
    setPagamentoSel(val);
    if (!cost) return;
    try {
      const { data } = await api.post(`/leads/${lead.id}/cost-estimate`, {
        distanza_km: dist === "" ? 0 : Number(dist),
        giorni: giorni === "" ? 1 : Number(giorni),
        prezzo: prezzo === "" ? undefined : Number(prezzo),
        ricarica,
        ricarica_id: ricarica ? (ricaricaId || null) : null,
        finanziamento,
        acconto: acconto === "" ? 0 : Number(acconto),
        durata_mesi: Number(durata),
        ...overrides(),
        pagamento: finanziamento ? null : (val || null),
        ...(pagamentoCustom ? { pagamento_custom: pagamentoCustom } : {}),
        bonus: bonusSel || null,
      });
      onUpdate(data);
      toast.success(pagamentoCustom ? "Piano di pagamento salvato" : "Modalità di pagamento aggiornata");
    } catch (e) {
      if (!pagamentoCustom) setPagamentoSel(prevSel);
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const saveBonus = async (val) => {
    setBonusSel(val);
    if (!cost) return;
    try {
      const { data } = await api.post(`/leads/${lead.id}/cost-estimate`, {
        distanza_km: dist === "" ? 0 : Number(dist),
        giorni: giorni === "" ? 1 : Number(giorni),
        prezzo: prezzo === "" ? undefined : Number(prezzo),
        ricarica,
        ricarica_id: ricarica ? (ricaricaId || null) : null,
        finanziamento,
        acconto: acconto === "" ? 0 : Number(acconto),
        durata_mesi: Number(durata),
        ...overrides(),
        pagamento: finanziamento ? null : (pagamentoSel || null),
        bonus: val || null,
      });
      onUpdate(data);
      toast.success("Bonus fiscale aggiornato");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const updatePrice = async () => {
    setSavingPrice(true);
    try {
      const { data } = await api.post(`/leads/${lead.id}/cost-estimate`, {
        distanza_km: dist === "" ? 0 : Number(dist),
        giorni: giorni === "" ? 1 : Number(giorni),
        prezzo: prezzo === "" ? undefined : Number(prezzo),
        ricarica,
        ricarica_id: ricarica ? (ricaricaId || null) : null,
        finanziamento,
        acconto: acconto === "" ? 0 : Number(acconto),
        durata_mesi: Number(durata),
        ...overrides(),
        pagamento: finanziamento ? null : (pagamentoSel || null),
        bonus: bonusSel || null,
      });
      onUpdate(data);
      toast.success("Prezzo aggiornato");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSavingPrice(false);
    }
  };

  const configSnap = useRef(null);
  configSnap.current = { dist, giorni, ricarica, ricaricaId, finanziamento, acconto, durata, pagamentoSel, bonusSel, cer, colore, nPannelli, nBatterie, contatore };
  const cfgTimer = useRef(null);

  const pushConfigNow = async (extra) => {
    const c = configSnap.current;
    setApplyingConfig(true);
    try {
      const { data } = await api.post(`/leads/${lead.id}/cost-estimate`, {
        ...(extra && extra.inverter_codice !== undefined ? { inverter_codice: extra.inverter_codice } : {}),
        distanza_km: c.dist === "" ? 0 : Number(c.dist),
        giorni: c.giorni === "" ? 1 : Number(c.giorni),
        ricarica: c.ricarica,
        ricarica_id: c.ricarica ? (c.ricaricaId || null) : null,
        finanziamento: c.finanziamento,
        acconto: c.acconto === "" ? 0 : Number(c.acconto),
        durata_mesi: Number(c.durata),
        pagamento: c.finanziamento ? null : (c.pagamentoSel || null),
        bonus: c.bonusSel || null,
        n_pannelli: c.nPannelli === "" ? null : Number(c.nPannelli),
        n_batterie: c.nBatterie === "" ? null : Number(c.nBatterie),
        cer: c.cer,
        pannello_colore: c.colore,
        contatore: c.contatore,
      });
      onUpdate(data);
      setPrezzo(data.cost_estimate?.prezzo ?? "");
      toast.success("Configurazione aggiornata");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setApplyingConfig(false);
    }
  };

  const scheduleConfig = () => {
    if (cfgTimer.current) clearTimeout(cfgTimer.current);
    cfgTimer.current = setTimeout(() => pushConfigNow(), 700);
  };

  const recalcColore = async (key, wp) => {
    const prevC = colore;
    setColore(key);
    setApplyingConfig(true);
    try {
      const useWp = (key === "total_black" || key === "ecored") && wp !== "" && wp != null;
      const { data } = await api.post(`/leads/${lead.id}/cost-estimate`, {
        distanza_km: dist === "" ? 0 : Number(dist),
        giorni: giorni === "" ? 1 : Number(giorni),
        ricarica,
        ricarica_id: ricarica ? (ricaricaId || null) : null,
        finanziamento,
        acconto: acconto === "" ? 0 : Number(acconto),
        durata_mesi: Number(durata),
        pagamento: finanziamento ? null : (pagamentoSel || null),
        bonus: bonusSel || null,
        ...overrides(),
        pannello_colore: key,
        colore_potenza_wp: useWp ? Number(wp) : null,
      });
      onUpdate(data);
      setPrezzo(data.cost_estimate?.prezzo ?? "");
      toast.success("Colore aggiornato — dati economici ricalcolati");
    } catch (e) {
      setColore(prevC);
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setApplyingConfig(false);
    }
  };

  const applyColore = (key) => recalcColore(key, null);

  const applyContatore = async (val) => {
    const prev = contatore;
    setContatore(val);
    setApplyingConfig(true);
    try {
      const { data } = await api.post(`/leads/${lead.id}/cost-estimate`, {
        distanza_km: dist === "" ? 0 : Number(dist),
        giorni: giorni === "" ? 1 : Number(giorni),
        ricarica, ricarica_id: ricarica ? (ricaricaId || null) : null,
        finanziamento, acconto: acconto === "" ? 0 : Number(acconto), durata_mesi: Number(durata),
        pagamento: finanziamento ? null : (pagamentoSel || null), bonus: bonusSel || null,
        n_pannelli: nPannelli === "" ? null : Number(nPannelli),
        n_batterie: nBatterie === "" ? null : Number(nBatterie),
        cer, pannello_colore: colore, contatore: val,
      });
      onUpdate(data);
      setPrezzo(data.cost_estimate?.prezzo ?? "");
      toast.success(val === "trifase" ? "Contatore trifase — accumulo forzato su batterie HV" : "Contatore monofase — regole batterie standard");
    } catch (e) {
      setContatore(prev);
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setApplyingConfig(false);
    }
  };

  const resetConfigAuto = async () => {
    setNPannelli("");
    setNBatterie("");
    setApplyingConfig(true);
    try {
      const { data } = await api.post(`/leads/${lead.id}/cost-estimate`, {
        distanza_km: dist === "" ? 0 : Number(dist),
        giorni: giorni === "" ? 1 : Number(giorni),
        ricarica,
        ricarica_id: ricarica ? (ricaricaId || null) : null,
        finanziamento,
        acconto: acconto === "" ? 0 : Number(acconto),
        durata_mesi: Number(durata),
        pagamento: finanziamento ? null : (pagamentoSel || null),
        bonus: bonusSel || null,
        n_pannelli: null,
        n_batterie: null,
        inverter_codice: "",
        cer,
        pannello_colore: colore,
      });
      onUpdate(data);
      setPrezzo(data.cost_estimate?.prezzo ?? "");
      toast.success("Dimensionamento automatico ripristinato");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setApplyingConfig(false);
    }
  };

  if (!pvReady && !canBypassBill) {
    return (
      <Card className="rounded border-border p-8 text-center" data-testid="preventivo-locked">
        <FileText className="h-8 w-8 mx-auto text-muted-foreground mb-3" strokeWidth={1.5} />
        <p className="text-sm text-muted-foreground">Completa prima l'<b>Analisi Preliminare</b> (dimensionamento kWp) nella scheda Profilazione per generare il preventivo.</p>
      </Card>
    );
  }

  const isCommerciale = cost?.gestione === "commerciale";
  const marginePct = cost ? Math.round((cost.margine || 0.35) * 100) : 35;
  const needsCf = (lead.entity_type || "persona_fisica") === "persona_fisica" && !((lead.codice_fiscale || "").trim());
  const needsPiva = (lead.entity_type || "persona_fisica") === "azienda" && !((lead.partita_iva || "").trim());

  return (
    <div className="space-y-5">
      {cost && (
        <div className="flex flex-wrap items-center gap-2" data-testid="preventivi-bar">
          <span className="text-xs uppercase tracking-widest text-muted-foreground mr-1">Preventivi</span>
          {preventivi.map((p) => {
            const isActive = p.id === activeId;
            return (
              <div key={p.id} className={`flex items-center rounded-full border pl-1 ${isActive ? "border-primary bg-primary/10" : "border-border"} ${p.hidden ? "opacity-50" : ""}`} data-testid={`preventivo-tab-${p.id}`}>
                {renaming === p.id ? (
                  <input autoFocus defaultValue={p.label} disabled={prevBusy}
                    onBlur={(e) => renamePrev(p.id, e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") renamePrev(p.id, e.target.value); if (e.key === "Escape") setRenaming(null); }}
                    className="bg-transparent text-sm font-medium px-2 py-1 w-32 outline-none" data-testid={`preventivo-rename-input-${p.id}`} />
                ) : (
                  <button type="button" onClick={() => switchPrev(p.id)} disabled={prevBusy} className={`pl-2 pr-1 py-1.5 text-sm font-medium ${isActive ? "text-primary" : "text-muted-foreground hover:text-foreground"}`} data-testid={`preventivo-tab-btn-${p.id}`}>
                    {prevBusy && !isActive ? <Loader2 className="h-3.5 w-3.5 animate-spin inline" /> : p.label}
                  </button>
                )}
                {canEdit && renaming !== p.id && (
                  <button type="button" onClick={() => toggleConsigliato(p.id)} disabled={prevBusy} title={p.consigliato ? "Rimuovi da consigliato" : "Segna come consigliato al cliente"} className={`px-1 ${p.consigliato ? "text-amber-500" : "text-muted-foreground hover:text-amber-500"}`} data-testid={`preventivo-consigliato-${p.id}`}><Star className="h-3.5 w-3.5" fill={p.consigliato ? "currentColor" : "none"} /></button>
                )}
                {canEdit && renaming !== p.id && preventivi.length > 1 && (
                  <button type="button" onClick={() => toggleVisibility(p.id)} disabled={prevBusy || isActive} title={isActive ? "Il preventivo attivo è sempre mostrato nella proposta" : (p.hidden ? "Nascosto dalla proposta — clicca per mostrarlo" : "Mostrato nella proposta — clicca per nasconderlo")} className={`px-1 disabled:opacity-40 ${p.hidden ? "text-muted-foreground/60 hover:text-primary" : "text-muted-foreground hover:text-primary"}`} data-testid={`preventivo-visibility-${p.id}`}>{p.hidden ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}</button>
                )}
                {canEdit && renaming !== p.id && (
                  <button type="button" onClick={() => setRenaming(p.id)} title="Rinomina" className="px-1 text-muted-foreground hover:text-primary" data-testid={`preventivo-rename-${p.id}`}><Pencil className="h-3 w-3" /></button>
                )}
                {canEdit && preventivi.length > 1 && (
                  <button type="button" onClick={() => deletePrev(p.id)} disabled={prevBusy} title="Elimina preventivo" className="pr-2 pl-0.5 text-muted-foreground hover:text-destructive" data-testid={`preventivo-delete-${p.id}`}><X className="h-3.5 w-3.5" /></button>
                )}
              </div>
            );
          })}
          {canEdit && (
            <Button variant="outline" size="sm" onClick={createPrev} disabled={prevBusy} className="rounded-full h-8 gap-1.5" data-testid="preventivo-add-btn">
              {prevBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Nuovo preventivo
            </Button>
          )}
        </div>
      )}
      {needsCf && (
        <div className="rounded border border-warning/50 bg-warning/10 p-4 flex items-start gap-3" data-testid="cf-missing-alert">
          <AlertTriangle className="h-5 w-5 text-warning mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold text-sm text-warning">Codice fiscale mancante</div>
            <p className="text-xs text-muted-foreground mt-0.5">
              Puoi comunque preventivare. Il codice fiscale sarà obbligatorio solo per generare e inviare la proposta e i documenti cliente. Inseriscilo qui, nell'anagrafica del lead oppure caricalo automaticamente analizzando la bolletta.
            </p>
            {canEdit && <CfQuickInput leadId={lead.id} onUpdate={onUpdate} />}
          </div>
        </div>
      )}
      {needsPiva && (
        <div className="rounded border border-warning/50 bg-warning/10 p-4 flex items-start gap-3" data-testid="piva-missing-alert">
          <AlertTriangle className="h-5 w-5 text-warning mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold text-sm text-warning">Partita IVA mancante</div>
            <p className="text-xs text-muted-foreground mt-0.5">
              Puoi comunque preventivare. La Partita IVA sarà obbligatoria solo per generare e inviare la proposta e i documenti cliente. Inseriscila nell'anagrafica del lead.
            </p>
          </div>
        </div>
      )}
      {contract && (
        <div className="rounded border border-success/40 bg-success/10 p-4 flex items-start gap-3" data-testid="contract-banner">
          <FileSignature className="h-5 w-5 text-success mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold text-sm text-success">Ordine contrattualizzato · Archiviato</div>
            <p className="text-xs text-muted-foreground mt-0.5">
              Data contratto: <b className="text-foreground" data-testid="contract-date">{new Date(contract.data_contratto).toLocaleDateString("it-IT")}</b> · Prezzo finale: <b className="text-foreground" data-testid="contract-price">{eur2(contract.prezzo_finale)}</b>
            </p>
          </div>
        </div>
      )}
      {activeLocked && (
        <div className="rounded border border-amber-500/50 bg-amber-500/10 p-4 flex items-start gap-3" data-testid="preventivo-locked-banner">
          <Lock className="h-5 w-5 text-amber-500 mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold text-sm text-amber-600">Preventivo bloccato — proposta inviata al cliente</div>
            <p className="text-xs text-muted-foreground mt-0.5">Per proporre una variante crea un <b>Nuovo preventivo</b>. Per ritoccare questa versione già inviata, sbloccala.</p>
            {activePrev.sent_at && <p className="text-[11px] text-muted-foreground mt-1" data-testid="preventivo-locked-sent-info">Inviata al cliente da <b className="text-foreground">{activePrev.sent_by || "—"}</b> · {fmtDT(activePrev.sent_at)}</p>}
          </div>
          {isAdmin ? (
            <Button size="sm" variant="outline" onClick={unlockPrev} disabled={unlocking} className="gap-1.5 shrink-0" data-testid="preventivo-unlock-btn">
              {unlocking ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Unlock className="h-3.5 w-3.5" />} Sblocca preventivo
            </Button>
          ) : (
            <span className="text-[11px] text-muted-foreground shrink-0 max-w-[200px] text-right" data-testid="preventivo-unlock-admin-only">Solo un amministratore può sbloccare il preventivo.</span>
          )}
        </div>
      )}
      {activePrev.sent_at && activePrev.dirty_since_send && !activeLocked && (
        <div className="rounded border border-orange-500/50 bg-orange-500/10 p-4 flex items-start gap-3" data-testid="preventivo-dirty-warning">
          <AlertTriangle className="h-5 w-5 text-orange-500 mt-0.5 shrink-0" />
          <div className="flex-1">
            <div className="font-semibold text-sm text-orange-600">La proposta inviata al cliente non è aggiornata</div>
            <p className="text-xs text-muted-foreground mt-0.5">Hai modificato questo preventivo dopo l'invio: il link e il PDF già inviati al cliente mostrano ancora la versione precedente. <b>Re-invia la proposta</b> (dalla scheda Outlook) per aggiornare link e PDF.</p>
          </div>
        </div>
      )}
      {activePrevEvents.length > 0 && (
        <div className="rounded border border-border bg-secondary/20 p-3" data-testid="preventivo-history">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Cronologia versione</div>
          <ul className="space-y-1">
            {activePrevEvents.slice().reverse().map((ev, i) => (
              <li key={i} className="flex items-center gap-2 text-xs text-muted-foreground" data-testid={`preventivo-history-item-${i}`}>
                {ev.action === "inviato" ? <Send className="h-3 w-3 text-primary shrink-0" /> : <Unlock className="h-3 w-3 text-amber-500 shrink-0" />}
                <span className="text-foreground font-medium">{ev.action === "inviato" ? "Proposta inviata al cliente" : "Preventivo sbloccato"}</span>
                {ev.by ? <span>· da <b className="text-foreground">{ev.by}</b></span> : null}
                {ev.at ? <span>· {fmtDT(ev.at)}</span> : null}
              </li>
            ))}
          </ul>
        </div>
      )}
      <Card className="rounded border-border p-5" data-testid="preventivo-inputs">
        <h2 className="font-heading font-semibold mb-1 flex items-center gap-2"><Calculator className="h-4 w-4 text-primary" strokeWidth={1.5} /> Calcolo costo commerciale</h2>
        <p className="text-xs text-muted-foreground mb-4">Impianto {num(cost?.pv_kwp ?? analysis.pv_kwp ?? 0)} kWp · Accumulo {num(cost?.storage_kwh ?? analysis.storage_kwh ?? 0)} kWh. Distanza e giorni calcolati automaticamente.{!pvReady && <span className="block mt-1 text-warning">Nessuna bolletta: preventivo avviato con 0 pannelli e 0 batterie, configurabili qui sotto.</span>}</p>

        {!reteEditor && (autoInfo?.distances || cost?.distances) && <InstallerDistancesBox distances={autoInfo?.distances || cost?.distances} lead={lead} onRecalc={pushConfigNow} canEdit={editable} />}

        <div className="rounded border border-border bg-secondary/20 p-4 mb-4" data-testid="ricarica-block">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Plug className="h-4 w-4 text-primary" strokeWidth={1.5} />
              <span className="text-sm font-medium">Stazione di ricarica desiderata</span>
            </div>
            <Switch checked={ricarica} onCheckedChange={setRicarica} disabled={!editable} data-testid="ricarica-switch" />
          </div>
          {ricarica && (
            <div className="mt-3 space-y-1.5">
              <label className="text-xs uppercase tracking-widest text-muted-foreground">Modello stazione</label>
              {ricariche.length === 0 ? (
                <p className="text-xs text-warning">Nessuna stazione di ricarica attiva nel catalogo. Aggiungila da Parametri → Prodotti.</p>
              ) : (
                <Select value={ricaricaId} onValueChange={setRicaricaId}>
                  <SelectTrigger data-testid="ricarica-select"><SelectValue placeholder="Seleziona stazione" /></SelectTrigger>
                  <SelectContent>
                    {ricariche.map((r) => (
                      <SelectItem key={r.id} value={r.id}>{r.model}{r.potenza_kw ? ` · ${r.potenza_kw} kW` : ""} — {eur(r.prezzo_eur)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          )}
        </div>

        <div className="rounded border border-primary/30 bg-primary/5 p-4 mb-4" data-testid="cer-block">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-primary" strokeWidth={1.5} />
              <span className="text-sm font-medium">Comunità Energetica Nazionale TORRI</span>
              {savingCer && <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />}
            </div>
            <Switch checked={cer} onCheckedChange={toggleCer} disabled={savingCer || !editable} data-testid="cer-switch" />
          </div>
          <p className="text-xs text-muted-foreground mt-3">
            <b className="text-primary">Beneficio</b>: accesso gratuito alla CER nazionale TORRI per i nostri clienti. Beneficio stimato massimo <b>0,05 €/kWh</b> sull'energia immessa in rete, che <b>aumenta il ritorno economico</b> senza incidere sul prezzo dell'impianto. L'entità effettiva dipende dalla configurazione (cabina primaria) e l'incentivo verrà governato <b>a consuntivo</b>.
          </p>
          {cer && (
            <div className="mt-3">
              <Button size="sm" onClick={computeCerBenefit} disabled={calcCer || savingCer} className="font-semibold" data-testid="calc-cer-benefit-btn">
                {calcCer ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Users className="h-4 w-4 mr-1.5" />} Calcola beneficio
              </Button>
              {cerBenefit && (
                <div className="mt-3 rounded border border-primary/30 bg-background/40 p-3 grid grid-cols-2 gap-3" data-testid="cer-benefit-result">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Beneficio in {cerBenefit.anni} anni</div>
                    <div className="text-2xl font-heading font-bold text-primary">{eur(cerBenefit.totale)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Media annua</div>
                    <div className="text-2xl font-heading font-bold">{eur(cerBenefit.annuo)}</div>
                  </div>
                  <p className="col-span-2 text-[11px] text-muted-foreground">Stima sull'energia immessa (fino a 0,05 €/kWh). Valore indicativo, governato a consuntivo.</p>
                </div>
              )}
            </div>
          )}
        </div>

        <Button onClick={compute} disabled={calc || !editable} data-testid="calc-cost-btn" className="font-semibold">
          {calc ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Calculator className="h-4 w-4 mr-2" />} {cost ? "Ricalcola costo" : "Calcola costo commerciale"}
        </Button>
        {needsCf && <p className="text-xs text-warning mt-2" data-testid="calc-cost-cf-note">Il codice fiscale non è richiesto per il preventivo, ma sarà obbligatorio per generare la proposta e i documenti cliente.</p>}
        {needsPiva && <p className="text-xs text-warning mt-2" data-testid="calc-cost-piva-note">La Partita IVA non è richiesta per il preventivo, ma sarà obbligatoria per generare e inviare la proposta e i documenti cliente.</p>}
      </Card>

      {cost && (
        <>
          {/* Routing banner (based on prezzo) — non per le pratiche rete */}
          {!isReteLive && (
          <div
            data-testid="gestione-banner"
            className={`rounded border p-4 flex items-start gap-3 ${isCommerciale ? "border-destructive/40 bg-destructive/10" : "border-success/40 bg-success/10"}`}
          >
            {isCommerciale ? <ArrowUpRight className="h-5 w-5 text-destructive mt-0.5" /> : <CheckCircle2 className="h-5 w-5 text-success mt-0.5" />}
            <div>
              <div className={`font-semibold text-sm ${isCommerciale ? "text-destructive" : "text-success"}`}>
                {isCommerciale ? "Oltre 60.000 € — Ufficio Commerciale" : "Fino a 60.000 € — Gestione Marketing"}
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">
                {isCommerciale
                  ? "Il prezzo supera la soglia di 60.000 €: il lead va trasferito all'Ufficio Commerciale per una consulenza mirata."
                  : "Il prezzo rientra nella soglia di 60.000 €: gestione diretta da parte del Marketing con preventivo immediato."}
              </p>
            </div>
          </div>
          )}

          {/* Costo commerciale */}
          <Card className="rounded border-border p-5" data-testid="preventivo-table">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="font-heading font-semibold flex items-center gap-2"><FileText className="h-4 w-4 text-primary" strokeWidth={1.5} /> Costo commerciale</h2>
                <p className="text-xs text-muted-foreground">{lead.name} · {num(cost.pv_kwp)} kWp · {num(cost.storage_kwh)} kWh</p>
                {cost.rete_partner && <span className="inline-block mt-1 text-[10px] uppercase tracking-wider text-primary border border-primary/40 rounded px-1.5 py-0.5" data-testid="rete-partner-badge">Scheda partner · prezzi riservati</span>}
              </div>
            </div>

            {cost.alerts && cost.alerts.length > 0 && (
              <div className="rounded border border-destructive/40 bg-destructive/10 p-3 mb-4 space-y-1" data-testid="preventivo-alerts">
                {cost.alerts.map((a, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm text-destructive font-medium" data-testid={`preventivo-alert-${i}`}>
                    <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" /> <span>{a}</span>
                  </div>
                ))}
              </div>
            )}

            {cost.componenti && (cost.componenti.pannello || cost.componenti.batteria) && (
              <div className="rounded border border-border bg-secondary/20 p-3 mb-4 text-sm space-y-3" data-testid="preventivo-componenti">
                {cost.componenti.pannello && (
                  <div className="flex items-center gap-2"><span className="text-xs uppercase tracking-widest text-muted-foreground w-24 shrink-0">Pannelli</span>
                    {photoByModel[cost.componenti.pannello.model] && <img src={photoByModel[cost.componenti.pannello.model]} alt={cost.componenti.pannello.model} className="h-9 w-9 rounded object-cover border border-border shrink-0" data-testid="preventivo-pannello-photo" />}
                    <span><b>{cost.componenti.pannello.n_pannelli}</b> × {cost.componenti.pannello.model} ({cost.componenti.pannello.potenza_wp} Wp) = <b>{num(cost.componenti.pannello.potenza_installata_kwp)} kWp</b></span></div>
                )}
                {cost.componenti.batteria && (
                  <div className="flex items-center gap-2"><span className="text-xs uppercase tracking-widest text-muted-foreground w-24 shrink-0">Accumulo</span>
                    {photoByModel[cost.componenti.batteria.model] && <img src={photoByModel[cost.componenti.batteria.model]} alt={cost.componenti.batteria.model} className="h-9 w-9 rounded object-cover border border-border shrink-0" data-testid="preventivo-batteria-photo" />}
                    <span><b>{cost.componenti.batteria.n_batterie}</b> × {cost.componenti.batteria.model} ({cost.componenti.batteria.tipo}) = <b>{num(cost.componenti.batteria.capacita_totale_kwh)} kWh</b></span></div>
                )}
                {cost.layout_lock && <LayoutLockBox ll={cost.layout_lock} lead={lead} onUpdate={onUpdate} canEdit={editable && !contract?.archived} />}
                {!contract?.archived && (
                  <div className="border-t border-border pt-3 space-y-2" data-testid="preventivo-config-override">
                    <div className="text-xs uppercase tracking-widest text-muted-foreground">Modifica quantità (configurazione)</div>
                    <div className="flex flex-wrap items-end gap-3">
                      <div className="space-y-1">
                        <label className="text-[10px] uppercase tracking-widest text-muted-foreground">N° pannelli</label>
                        <Input type="number" min="0" value={nPannelli} onChange={(e) => { setNPannelli(e.target.value); scheduleConfig(); }} disabled={!editable || applyingConfig || !!cost.layout_lock} className="w-28 h-9" data-testid="config-n-pannelli" placeholder="auto" title={cost.layout_lock ? "Fissato dal layout: modifica il layout per cambiare i pannelli" : undefined} />
                      </div>
                      <div className="space-y-1">
                        <label className="text-[10px] uppercase tracking-widest text-muted-foreground">N° batterie <span className="normal-case text-muted-foreground/70">(0 = senza accumulo)</span></label>
                        <Input type="number" min="0" value={nBatterie} onChange={(e) => { setNBatterie(e.target.value); scheduleConfig(); }} disabled={!editable || applyingConfig} className="w-28 h-9" data-testid="config-n-batterie" placeholder="auto" />
                      </div>
                      <div className="space-y-1">
                        <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Contatore</label>
                        <Select value={contatore} onValueChange={applyContatore} disabled={!editable || applyingConfig}>
                          <SelectTrigger className="w-40 h-9" data-testid="config-contatore"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="monofase" data-testid="contatore-opt-monofase">Monofase</SelectItem>
                            <SelectItem value="trifase" data-testid="contatore-opt-trifase">Trifase</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {(cost.inverter_choices || []).length > 0 && (
                        <div className="space-y-1">
                          <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Inverter (da listino)</label>
                          <Select value={cost.inverter_codice || "__auto"} onValueChange={(v) => pushConfigNow({ inverter_codice: v === "__auto" ? "" : v })} disabled={!editable || applyingConfig}>
                            <SelectTrigger className="w-72 h-9" data-testid="config-inverter"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__auto" data-testid="inverter-opt-auto">Automatico (consigliato)</SelectItem>
                              {cost.inverter_choices.map((o) => <SelectItem key={o.codice} value={o.codice} data-testid={`inverter-opt-${o.codice}`}>{o.codice} · {o.potenza_kw} kW</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                      <Button size="sm" variant="ghost" onClick={resetConfigAuto} disabled={applyingConfig || !editable} data-testid="reset-config-btn">Ripristina automatico</Button>
                      {applyingConfig && <span className="text-xs text-muted-foreground inline-flex items-center gap-1.5" data-testid="config-updating"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Aggiornamento…</span>}
                    </div>
                    <p className="text-[11px] text-muted-foreground">Le modifiche si applicano automaticamente al preventivo: non serve ricalcolare. Il dimensionamento consigliato dai consumi resta invariato; le analisi di spesa e rendimento seguiranno questa configurazione. Imposta <b>0 batterie</b> per proporre l'impianto senza accumulo. Regola batterie: LV per 1-2, HV per 3+ (sistema HV minimo 2 batterie). Con contatore <b>trifase</b> l'accumulo viene forzato su batterie <b>HV</b>. L'inverter è scelto in automatico; puoi sostituirlo con un altro modello del listino. Il contatore è pre-impostato in automatico dalla potenza letta in bolletta (≤6 kW → monofase).</p>
                  </div>
                )}
                {contract?.archived && <p className="text-xs text-muted-foreground pt-1">Potenza e accumulo dimensionati a multipli dei moduli dei Prodotti di flusso attivi.</p>}
              </div>
            )}

            <div className="flex items-center justify-between mb-2">
              <p className="text-xs uppercase tracking-widest text-muted-foreground">Voci di costo</p>
              <div className="flex flex-wrap items-center gap-2">
              {!editCosts && (
                  <Button size="sm" variant="outline" onClick={downloadCostSheet} disabled={sheetBusy} data-testid="cost-sheet-pdf-btn">{sheetBusy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <FileDown className="h-3.5 w-3.5 mr-1.5" />}Scarica PDF scheda di costo</Button>
              )}
              {(isProject || reteEditor) && editable && !contract?.archived && (
                editCosts ? (
                  <div className="flex gap-2">
                    <Button size="sm" variant="ghost" onClick={() => { setEditCosts(false); setCostOv(cost?.item_overrides || {}); setExtras(cost?.extra_items || []); }} disabled={savingCosts} data-testid="costs-cancel-btn">Annulla</Button>
                    <Button size="sm" onClick={saveCosts} disabled={savingCosts} className="font-semibold" data-testid="costs-save-btn">{savingCosts ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null}Salva costi</Button>
                  </div>
                ) : (
                  <Button size="sm" variant="outline" onClick={() => setEditCosts(true)} data-testid="costs-edit-btn"><Pencil className="h-3.5 w-3.5 mr-1.5" />Modifica costi</Button>
                )
              )}
              </div>
            </div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent border-border">
                    <TableHead className="text-xs uppercase">Voce</TableHead>
                    <TableHead className="text-xs uppercase text-right">Q.tà</TableHead>
                    <TableHead className="text-xs uppercase text-right">Costo cad.</TableHead>
                    <TableHead className="text-xs uppercase text-right">Importo</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {!editCosts && cost.items.map((it, i) => (
                    <TableRow key={i} className={`border-border ${it.evidenzia ? "bg-warning/5" : ""} ${it.extra ? "bg-primary/5" : ""} ${it.modificato ? "bg-primary/5" : ""}`} data-testid={`cost-row-${i}`}>
                      <TableCell>
                        <div className="font-medium flex items-center gap-1.5">{it.evidenzia && <AlertTriangle className="h-3.5 w-3.5 text-warning" />}{it.voce}{(it.extra || it.modificato) && <span className="text-[9px] uppercase tracking-wider text-primary border border-primary/40 rounded px-1">{it.extra ? "extra" : "modif."}</span>}</div>
                        <div className="text-xs text-muted-foreground">{it.dettaglio}</div>
                        {it.nota && <div className="text-xs text-warning mt-0.5 max-w-md">{it.nota}</div>}
                      </TableCell>
                      <TableCell className="text-right font-heading whitespace-nowrap">{num(it.quantita)} {it.unita}</TableCell>
                      <TableCell className="text-right whitespace-nowrap">{eur(it.prezzo_unitario)}</TableCell>
                      <TableCell className="text-right font-heading whitespace-nowrap">{eur(it.importo)}</TableCell>
                    </TableRow>
                  ))}
                  {editCosts && cost.items.filter((it) => !it.extra).map((it, i) => {
                    const q = ovVal(it.voce, "quantita", it.quantita);
                    const pu = ovVal(it.voce, "prezzo_unitario", it.prezzo_unitario);
                    return (
                      <TableRow key={`e${i}`} className="border-border" data-testid={`cost-edit-row-${i}`}>
                        <TableCell><div className="font-medium">{it.voce}</div><div className="text-xs text-muted-foreground">{it.dettaglio}</div></TableCell>
                        <TableCell className="text-right"><Input type="number" step="0.01" className="h-8 w-24 ml-auto text-right" value={q} disabled={isReteLive} title={isReteLive ? "Quantità calcolata dalla configurazione: non modificabile" : undefined} onChange={(e) => setOv(it.voce, "quantita", e.target.value)} data-testid={`cost-q-${i}`} /></TableCell>
                        <TableCell className="text-right"><Input type="number" step="0.01" className="h-8 w-28 ml-auto text-right" value={pu} disabled={!!it.riservato} title={it.riservato ? "Prezzo riservato partner: non modificabile" : undefined} onChange={(e) => setOv(it.voce, "prezzo_unitario", e.target.value)} data-testid={`cost-pu-${i}`} /></TableCell>
                        <TableCell className="text-right font-heading whitespace-nowrap">{eur((Number(q) || 0) * (Number(pu) || 0))}</TableCell>
                      </TableRow>
                    );
                  })}
                  {editCosts && extras.map((e, i) => (
                    <TableRow key={`x${i}`} className="border-border bg-primary/5" data-testid={`cost-extra-row-${i}`}>
                      <TableCell><Input placeholder="Voce aggiuntiva" className="h-8" value={e.voce} onChange={(ev) => setExtra(i, "voce", ev.target.value)} data-testid={`extra-voce-${i}`} /></TableCell>
                      <TableCell className="text-right"><Input type="number" step="0.01" className="h-8 w-24 ml-auto text-right" value={e.quantita} onChange={(ev) => setExtra(i, "quantita", ev.target.value)} data-testid={`extra-q-${i}`} /></TableCell>
                      <TableCell className="text-right"><Input type="number" step="0.01" className="h-8 w-28 ml-auto text-right" value={e.prezzo_unitario} disabled={!!e.codice} onChange={(ev) => setExtra(i, "prezzo_unitario", ev.target.value)} data-testid={`extra-pu-${i}`} /></TableCell>
                      <TableCell className="text-right whitespace-nowrap"><div className="flex items-center justify-end gap-2"><span className="font-heading">{eur((Number(e.quantita) || 0) * (Number(e.prezzo_unitario) || 0))}</span><button type="button" onClick={() => removeExtra(i)} data-testid={`extra-remove-${i}`}><Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive" /></button></div></TableCell>
                    </TableRow>
                  ))}
                  {editCosts && (
                    <TableRow className="hover:bg-transparent border-none">
                      <TableCell colSpan={4}>
                        <div className="flex flex-wrap items-center gap-2">
                          <Button size="sm" variant="ghost" onClick={addExtra} data-testid="extra-add-btn"><Plus className="h-4 w-4 mr-1.5" />Aggiungi voce libera</Button>
                          {listino?.length > 0 && (
                            <>
                              <ListinoPicker listino={listino} value={pickCod} onChange={setPickCod} eur={eur} />
                              <Button size="sm" variant="outline" disabled={!pickCod} data-testid="listino-add-btn" onClick={() => {
                                const x = listino.find((l) => l.codice === pickCod);
                                if (x) setExtras((p) => [...p, { voce: `${x.codice} · ${(x.descrizione || "").slice(0, 80)}`, codice: x.codice, quantita: 1, prezzo_unitario: x.prezzo_riservato, unita: x.um || "pz" }]);
                                setPickCod("");
                              }}><Plus className="h-4 w-4 mr-1.5" />Aggiungi articolo</Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                  {cost.manutenzione?.incluso && !editCosts && (
                    <TableRow className="border-border bg-amber-500/5" data-testid="cost-man-incluso-row">
                      <TableCell colSpan={3}><div className="font-medium">Manutenzione programmata (inclusa · {cost.manutenzione.anni || 0} anni)</div><div className="text-xs text-muted-foreground">Servizio incluso nell'offerta · {cost.manutenzione.anni || 0} × {eur(cost.manutenzione.costo_annuo)}/anno · incide sul margine, non sul prezzo</div></TableCell>
                      <TableCell className="text-right font-heading whitespace-nowrap">{eur(cost.manutenzione_costo_incluso ?? cost.manutenzione.costo_incluso_totale ?? cost.manutenzione.costo_annuo)}</TableCell>
                    </TableRow>
                  )}
                  <TableRow className="border-border bg-secondary/30 hover:bg-secondary/30">
                    <TableCell colSpan={3} className="font-bold text-right">Costo commerciale totale</TableCell>
                    <TableCell className="text-right font-heading font-bold text-lg" data-testid="cost-total">{eur(editCosts
                      ? cost.items.filter((it) => !it.extra).reduce((s, it) => s + (Number(ovVal(it.voce, "quantita", it.quantita)) || 0) * (Number(ovVal(it.voce, "prezzo_unitario", it.prezzo_unitario)) || 0), 0) + extras.reduce((s, e) => s + (Number(e.quantita) || 0) * (Number(e.prezzo_unitario) || 0), 0)
                      : (cost.manutenzione?.incluso ? (cost.costo_industriale_effettivo ?? cost.costo_industriale) : cost.costo_industriale))}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
            <p className="text-xs text-muted-foreground mt-3">
              Costo commerciale (IVA esclusa). Sicurezza: escluso ogni intervento di sicurezza permanente ed extra (ponteggi, linee vita extra). Inclusi solo parapetti mobili e sollevamenti tradizionali.
            </p>
          </Card>

          {/* Giorni di cantiere — mostrati dopo le distanze e la scheda di costo */}
          <Card className="rounded border-border p-5" data-testid="giorni-cantiere-card">
            <h2 className="font-heading font-semibold mb-1 flex items-center gap-2"><Wrench className="h-4 w-4 text-primary" strokeWidth={1.5} /> Giorni di cantiere</h2>
            <p className="text-xs text-muted-foreground mb-3">Calcolati automaticamente dalla manodopera del preventivo. Modifica il valore e premi <b>Applica</b> per ricalcolare le incidenze nella scheda costi.</p>
            <div className="rounded border border-border bg-secondary/20 p-3 sm:max-w-md">
              <div className="flex items-center gap-2">
                <Input type="number" min="0" value={giorni} disabled={!canEdit}
                  onChange={(e) => { setGiorni(e.target.value); setGiorniAuto(false); }}
                  data-testid="cost-giorni-input" placeholder="Automatico" />
                {!giorniAuto && canEdit && (
                  <Button size="sm" className="shrink-0 font-semibold" data-testid="giorni-apply-btn" disabled={calc}
                    onClick={() => compute({ giorniAuto: false })}>
                    {calc ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null} Applica
                  </Button>
                )}
                {!giorniAuto && canEdit && (
                  <Button variant="outline" size="sm" className="shrink-0" data-testid="giorni-auto-reset" disabled={calc}
                    onClick={() => { setGiorniAuto(true); compute({ giorniAuto: true }); }}>Auto</Button>
                )}
              </div>
              <p className="text-[10px] text-muted-foreground mt-1">
                {giorniAuto
                  ? <>Automatico: manodopera ÷ 35 €/h ÷ 3 persone ÷ 8 h/giorno{autoInfo ? ` (${eur(autoInfo.manodopera_tot)} manodopera)` : ""}. Modifica il valore e premi <b>Applica</b> per impostarlo manualmente.</>
                  : <>Valore impostato manualmente — premi <b>Applica</b> per ricalcolare le incidenze, oppure <b>Auto</b> per tornare al calcolo automatico.</>}
              </p>
            </div>
          </Card>

          {/* Prezzo di vendita */}
          <Card className="rounded border-border p-5" data-testid="prezzo-card">
            <h2 className="font-heading font-semibold mb-1 flex items-center gap-2"><TrendingUp className="h-4 w-4 text-primary" strokeWidth={1.5} /> Prezzo di vendita</h2>
            <p className="text-xs text-muted-foreground mb-4">Prezzo base suggerito applicando un <b>margine del {marginePct}%</b> sul costo commerciale (prezzo = costo / (1 − margine)). Modificabile.</p>
            <div className="grid sm:grid-cols-2 gap-4 items-end">
              <div className="border border-border rounded p-4">
                <div className="text-xs uppercase tracking-widest text-muted-foreground">Prezzo suggerito (margine {marginePct}%)</div>
                <div className="mt-2 text-2xl font-heading font-bold" data-testid="prezzo-suggerito">{eur(cost.prezzo_suggerito)}</div>
              </div>
              <div className="space-y-1.5">
                <label className="text-xs uppercase tracking-widest text-muted-foreground">Prezzo proposto (€)</label>
                <div className="flex gap-2">
                  <Input type="number" value={prezzo} onChange={(e) => setPrezzo(e.target.value)} data-testid="cost-prezzo-input" placeholder={String(cost.prezzo_suggerito)} />
                  <Button onClick={updatePrice} disabled={savingPrice} data-testid="update-price-btn" className="font-semibold shrink-0">
                    {savingPrice && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Aggiorna
                  </Button>
                </div>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-3">
              Prezzo attuale: <b className="text-foreground" data-testid="prezzo-attuale">{eur(cost.prezzo)}</b> · Margine effettivo: {cost.prezzo > 0 ? Math.round(((cost.prezzo - (cost.manutenzione?.incluso ? (cost.costo_industriale_effettivo ?? cost.costo_industriale) : cost.costo_industriale)) / cost.prezzo) * 100) : 0}%{cost.manutenzione?.incluso ? " (manutenzione inclusa)" : ""}
            </p>

            {cost.prezzo_min != null && !reteEditor && (
              <div className="mt-3 flex items-center justify-between rounded border border-amber-500/30 bg-amber-500/10 px-3 py-2" data-testid="prezzo-minimo-block">
                <span className="text-xs uppercase tracking-widest text-amber-600 font-bold">
                  Prezzo minimo impianto{cost.margine_min_pct != null ? ` (margine ${Math.round(cost.margine_min_pct)}%)` : ""}
                </span>
                <span className="font-heading font-bold" data-testid="prezzo-minimo">{eur(cost.prezzo_min)}</span>
              </div>
            )}

            {/* IVA 10% */}
            <div className="mt-4 pt-4 border-t border-border space-y-1.5 text-sm" data-testid="iva-block">
              <div className="flex items-center justify-between"><span className="text-muted-foreground">Imponibile</span><span className="font-heading">{eur2(cost.prezzo)}</span></div>
              <div className="flex items-center justify-between"><span className="text-muted-foreground">IVA ({Math.round((cost.iva_rate ?? 0.1) * 100)}%)</span><span className="font-heading" data-testid="iva-importo">{eur2(cost.iva_importo)}</span></div>
              <div className="flex items-center justify-between pt-1 border-t border-border/60"><span className="font-bold">Totale IVA inclusa</span><span className="font-heading font-bold text-lg text-primary" data-testid="prezzo-iva-incluso">{eur2(cost.prezzo_iva_incluso)}</span></div>
            </div>
          </Card>

          {/* Manutenzione programmata */}
          <Card className="rounded border-border p-5" data-testid="manutenzione-card">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2"><Wrench className="h-4 w-4 text-primary" /><span className="text-sm font-semibold">Manutenzione programmata</span></div>
              <Switch checked={manAttiva} onCheckedChange={(v) => {
                setManAttiva(v);
                if (v) {
                  // Riattivazione: si perdono i dati precedenti e si ripropongono i valori da Parametri.
                  const defAtt = (manCfg?.attivita || []).filter((a) => a.active).map((a) => a.key);
                  const defMod = manCfg?.modalita || "annuo";
                  setManAtt(defAtt); setManMod(defMod); setManPa(""); setManPk(""); setManIncluso(false); setManAnni(1);
                  saveManutenzione({ manutenzione_attiva: true, manutenzione_reset: true, manutenzione_attivita: defAtt, manutenzione_modalita: defMod, manutenzione_prezzo_annuo: null, manutenzione_prezzo_kwp: null, manutenzione_incluso: false, manutenzione_anni: 1 });
                } else {
                  saveManutenzione({ manutenzione_attiva: false });
                }
              }} data-testid="manutenzione-switch" />
            </div>
            <p className="text-xs text-muted-foreground mb-3">Voce economica opzionale. Se <b>non inclusa</b> incide sui flussi del piano finanziario (la paga il cliente); se <b>inclusa nell'offerta</b> entra nella scheda di costo (riduce il margine) ma <b>non</b> nel prezzo.</p>
            {manAttiva && (
              <div className="space-y-3">
                {cost?.pv_kwp != null && (() => {
                  const manSoglia = manCfg?.soglia_kwp_diretta ?? 20;
                  const sopra = cost.pv_kwp > manSoglia;
                  return (
                    <>
                      {sopra && (
                        <div className="flex flex-wrap items-end gap-2" data-testid="man-gestione-row">
                          <div className="w-64">
                            <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Gestione manutenzione (oltre {manSoglia} kWp)</label>
                            <Select value={manGestione} onValueChange={(v) => { setManGestione(v); saveManutenzione({ manutenzione_gestione: v }); }}>
                              <SelectTrigger data-testid="man-gestione"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="diretta">Diretta (Torri.Solar) · + trasferta da sede</SelectItem>
                                <SelectItem value="partner">Tramite Partner di zona</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      )}
                      {!isReteLive && (!sopra || manGestione === "partner") && (
                        <div className="rounded border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs" data-testid="man-gestione-partner-note">
                          {!sopra
                            ? <>Impianto ≤ {manSoglia} kWp: la manutenzione è <b>sempre affidata al Partner di zona</b> ed è <b>subordinata alla sua accettazione</b>. Trasferta calcolata sulla <b>media dei 3 partner più vicini</b>.</>
                            : <>Gestione <b>tramite Partner di zona</b>: il servizio è <b>subordinato all'accettazione del partner</b>. Trasferta calcolata sulla <b>media dei 3 partner più vicini</b>.</>}
                        </div>
                      )}
                    </>
                  );
                })()}
                <label className="flex items-center justify-between gap-3 rounded border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-sm cursor-pointer" data-testid="man-incluso-row">
                  <span className="font-medium">Considera incluso nell'offerta<span className="block text-[11px] text-muted-foreground font-normal">Il valore entra nel costo e nel margine (non nel prezzo). Nei documenti comparirà "incluso".</span></span>
                  <Switch checked={manIncluso} onCheckedChange={(v) => { setManIncluso(v); saveManutenzione({ manutenzione_incluso: v }); }} data-testid="man-incluso-switch" />
                </label>
                {manIncluso && (
                  <div className="flex flex-wrap items-end gap-2" data-testid="man-anni-row">
                    <div className="w-32">
                      <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Anni inclusi</label>
                      <Input type="number" min="1" step="1" value={manAnni} onChange={(e) => setManAnni(e.target.value)} data-testid="man-anni-input" />
                    </div>
                    <Button size="sm" variant="outline" onClick={() => saveManutenzione()} disabled={savingMan} data-testid="man-anni-apply" className="font-semibold">Applica anni</Button>
                    <p className="text-[11px] text-muted-foreground pb-2">Costo incluso = costo annuo × anni. Per il cliente è gratis nei primi {manAnni || 1} anni.</p>
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {(manCfg?.attivita || []).filter((a) => a.active).map((a) => (
                    <label key={a.key} className="flex items-center gap-2 text-sm cursor-pointer">
                      <input type="checkbox" checked={manAtt.includes(a.key)} onChange={(e) => setManAtt((arr) => e.target.checked ? [...arr, a.key] : arr.filter((k) => k !== a.key))} data-testid={`man-att-${a.key}`} />
                      <span>{a.label}</span>
                    </label>
                  ))}
                </div>
                <div className="flex flex-wrap items-end gap-2">
                  <div className="w-36">
                    <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Modalità prezzo</label>
                    <Select value={manMod} onValueChange={setManMod}>
                      <SelectTrigger data-testid="man-modalita"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="annuo">€ / anno</SelectItem><SelectItem value="kwp">€ / kWp</SelectItem></SelectContent>
                    </Select>
                  </div>
                  {manMod === "kwp" ? (
                    <div className="w-32"><label className="text-[10px] uppercase tracking-widest text-muted-foreground">€ / kWp</label><Input type="number" value={manPk} onChange={(e) => setManPk(e.target.value)} placeholder={String(manCfg?.prezzo_kwp ?? 0)} data-testid="man-prezzo-kwp" /></div>
                  ) : (
                    <div className="w-32"><label className="text-[10px] uppercase tracking-widest text-muted-foreground">€ / anno</label><Input type="number" value={manPa} onChange={(e) => setManPa(e.target.value)} placeholder={String(manCfg?.prezzo_annuo ?? 0)} data-testid="man-prezzo-annuo" /></div>
                  )}
                  <Button size="sm" onClick={() => saveManutenzione()} disabled={savingMan} data-testid="man-save" className="font-semibold">{savingMan ? <Loader2 className="h-4 w-4 animate-spin" /> : "Applica"}</Button>
                </div>
                {cost?.manutenzione?.attiva && (
                  <div className="rounded border border-primary/20 bg-primary/5 p-3 text-sm space-y-2" data-testid="man-allinclusive">
                    <div className="text-[10px] uppercase tracking-widest text-primary font-semibold">Manutenzione Programmata All Inclusive</div>
                    <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs">
                      <div className="text-muted-foreground">Fascia applicata</div><div className="text-right font-medium">{cost.manutenzione.su_preventivo ? "Su preventivo" : (cost.manutenzione.fascia || "—")}</div>
                      <div className="text-muted-foreground">Gestione</div><div className="text-right font-medium">{cost.manutenzione.gestione === "partner" ? "Tramite Partner di zona" : "Diretta (Torri.Solar)"}</div>
                      <div className="text-muted-foreground">Canone base annuo</div><div className="text-right font-medium">{cost.manutenzione.su_preventivo ? "—" : eur(cost.manutenzione.canone_base)}</div>
                      {cost.distances?.nearest_partner_km != null && (
                        <><div className="text-muted-foreground">Partner più vicino</div><div className="text-right font-medium">{cost.distances.partners?.[0]?.ragione_sociale || "—"} · {Math.round(cost.distances.nearest_partner_km)} km</div></>
                      )}
                      {cost.manutenzione.trasferta && (
                        <>
                          <div className="text-muted-foreground">Base trasferta</div><div className="text-right font-medium">{cost.manutenzione.trasferta.basis === "media_3_partner" ? `Media ${cost.manutenzione.trasferta.n_partner_media || 3} partner più vicini` : cost.manutenzione.trasferta.basis === "sede" ? "Distanza da sede" : "Partner più vicino"} · {Math.round(cost.manutenzione.trasferta.nearest_km || 0)} km</div>
                          <div className="text-muted-foreground">Km eccedenti (A/R)</div><div className="text-right font-medium">{cost.manutenzione.trasferta.km_eccedenti} km → {cost.manutenzione.trasferta.km_addebitabili} km</div>
                          <div className="text-muted-foreground">Costo trasferta</div><div className="text-right font-medium">{eur(cost.manutenzione.trasferta.costo)}{cost.manutenzione.trasferta.fallback_hq ? " (fallback sede)" : ""}</div>
                        </>
                      )}
                    </div>
                    <div className="flex items-center justify-between border-t border-border pt-2">
                      <span className="text-xs text-muted-foreground">Prezzo finale annuale (IVA 10% incl.)</span>
                      <span className="font-heading font-bold text-primary" data-testid="man-prezzo-finale">{cost.manutenzione.su_preventivo ? "Su preventivo" : eur(cost.manutenzione.prezzo_finale_annuo_iva)}</span>
                    </div>
                    {cost.manutenzione.trasferta?.verifica_commerciale && <div className="text-[11px] text-amber-600">⚠ Verifica commerciale necessaria (costo trasferta al limite massimo)</div>}
                    <div className="text-[11px] text-muted-foreground">€/kWp: {eur2(cost.manutenzione.prezzo_kwp)}</div>
                  </div>
                )}
              </div>
            )}
          </Card>

          {/* Finanziamento Findomestic / Leasing (Progetti) */}
          {!isReteLive && (
          <Card className="rounded border-border p-5" data-testid="finanziamento-card">
            <div className="flex items-center justify-between gap-3 mb-1">
              <h2 className="font-heading font-semibold flex items-center gap-2"><Landmark className="h-4 w-4 text-primary" strokeWidth={1.5} /> {finLabel}</h2>
              <Switch checked={finanziamento} onCheckedChange={setFinanziamento} data-testid="finanziamento-switch" />
            </div>
            {isProject ? (
              <p className="text-xs text-muted-foreground mb-4">Leasing / noleggio operativo dedicato ai <b>Progetti</b>, da 36 a 120 mensilità, tasso <b>{Number(leasingTasso).toLocaleString("it-IT")}%</b>. L'importo è il totale <b>IVA inclusa</b> al netto dell'acconto. Le condizioni e il disclaimer (subordinato all'ottenimento, pratica avviata dopo la firma, contratto valido solo dopo l'ottenimento) sono in <b>Parametri › Condizioni › Pagamenti</b> e compaiono nell'Outlook e nel PDF.</p>
            ) : (
              <p className="text-xs text-muted-foreground mb-4">Finanziamento fino a 60.000 €, da 36 a 120 mensilità, tasso 6,75% (più eventuali spese). L'importo finanziato è il totale <b>IVA inclusa</b> al netto dell'acconto.</p>
            )}

            {finanziamento && (
              <>
                <div className="grid sm:grid-cols-2 gap-4 mb-4">
                  <div className="space-y-1.5">
                    <label className="text-xs uppercase tracking-widest text-muted-foreground">Acconto (€)</label>
                    <Input type="number" value={acconto} onChange={(e) => setAcconto(e.target.value)} data-testid="acconto-input" placeholder="0" />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-xs uppercase tracking-widest text-muted-foreground">Durata (mesi)</label>
                    <Select value={durata} onValueChange={setDurata}>
                      <SelectTrigger data-testid="durata-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {[36, 48, 60, 72, 84, 96, 108, 120].map((m) => <SelectItem key={m} value={String(m)}>{m} mesi</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Button onClick={updatePrice} disabled={savingPrice} data-testid="calc-finanziamento-btn" variant="outline" size="sm" className="mb-4">
                  {savingPrice ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Landmark className="h-4 w-4 mr-2" />} Ricalcola finanziamento
                </Button>

                {cost.finanziamento?.attivo && cost.finanziamento?.rata_mensile != null ? (
                  <div className="rounded border border-primary/30 bg-primary/5 p-4" data-testid="finanziamento-result">
                    {!cost.finanziamento.entro_soglia && (
                      <p className="text-xs text-destructive mb-3 flex items-center gap-1.5"><AlertTriangle className="h-3.5 w-3.5" /> Importo da finanziare oltre 60.000 €: non ammissibile con Findomestic.</p>
                    )}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                      <Stat label="Importo finanziato" value={eur2(cost.finanziamento.importo_finanziato)} testid="fin-importo" />
                      <Stat label="Durata" value={`${cost.finanziamento.durata_mesi} mesi`} />
                      <Stat label="Rata mensile" value={eur2(cost.finanziamento.rata_mensile)} testid="fin-rata" big />
                      <Stat label="Totale dovuto" value={eur2(cost.finanziamento.totale_dovuto)} testid="fin-totale" />
                    </div>
                    <p className="text-xs text-muted-foreground mt-3">Acconto: <b>{eur2(cost.finanziamento.acconto)}</b> · Tasso annuo (TAN): {Number(cost.finanziamento.tasso_annuo * 100).toLocaleString("it-IT")}%. Valori indicativi (TAEG e spese secondo condizioni Findomestic).</p>
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">Imposta acconto e durata, poi ricalcola per vedere la rata.</p>
                )}
              </>
            )}
          </Card>
          )}

          {/* Modalità di pagamento */}
          <Card className="rounded border-border p-5" data-testid="pagamento-card">
            <h2 className="font-heading font-semibold flex items-center gap-2 mb-1"><CreditCard className="h-4 w-4 text-primary" strokeWidth={1.5} /> Modalità di pagamento</h2>
            {finanziamento ? (
              <p className="text-xs text-muted-foreground" data-testid="pagamento-findomestic-note">Con il finanziamento attivo, la modalità di pagamento è automaticamente <b>{finLabel}</b> e viene richiamata nei documenti e nell'Outlook.</p>
            ) : (
              <>
                <p className="text-xs text-muted-foreground mb-3">Seleziona la forma di pagamento da richiamare nel preventivo e nell'Outlook. Le voci sono modificabili in <b>Parametri › Condizioni</b>.</p>
                <Select value={pagamentoSel} onValueChange={savePagamento} disabled={activeLocked}>
                  <SelectTrigger data-testid="pagamento-select" className="max-w-sm"><SelectValue placeholder="Seleziona modalità" /></SelectTrigger>
                  <SelectContent>
                    {pagamentiList.map((p) => <SelectItem key={p.titolo} value={p.titolo}>{p.titolo}</SelectItem>)}
                    {isReteLive && <SelectItem value={CUSTOM_PAY} data-testid="pagamento-custom-option">{CUSTOM_PAY}</SelectItem>}
                  </SelectContent>
                </Select>
                {pagamentoSel === CUSTOM_PAY && (
                  <>
                    <CustomPaymentEditor value={cost?.pagamento_custom} totale={cost?.prezzo_iva_incluso || 0} disabled={activeLocked} onSave={(pc) => savePagamento(CUSTOM_PAY, pc)} />
                    {cost?.pagamento_custom?.testo && <p className="text-xs text-muted-foreground mt-3 leading-relaxed" data-testid="pagamento-testo">{reteBrand(cost.pagamento_custom.testo)}</p>}
                  </>
                )}
                {pagamentoSel !== CUSTOM_PAY && pagamentiList.find((p) => p.titolo === pagamentoSel) && (
                  <p className="text-xs text-muted-foreground mt-3 leading-relaxed" data-testid="pagamento-testo">{reteBrand(pagamentiList.find((p) => p.titolo === pagamentoSel).testo)}</p>
                )}
              </>
            )}
          </Card>

          {/* Bonus fiscale */}
          <Card className="rounded border-border p-5" data-testid="bonus-card">
            <h2 className="font-heading font-semibold flex items-center gap-2 mb-1"><Percent className="h-4 w-4 text-primary" strokeWidth={1.5} /> Bonus fiscale</h2>
            <p className="text-xs text-muted-foreground mb-3">La detrazione selezionata viene applicata ai calcoli di rientro economico, risparmio e grafici. Le voci sono modificabili in <b>Parametri › Condizioni</b>.</p>
            <Select value={bonusSel} onValueChange={saveBonus}>
              <SelectTrigger data-testid="bonus-select" className="max-w-sm"><SelectValue placeholder="Seleziona bonus" /></SelectTrigger>
              <SelectContent>
                {bonusList.map((b) => <SelectItem key={b.nome} value={b.nome}>{b.nome}{b.percentuale ? ` — ${b.percentuale}% in ${b.anni} anni` : ""}</SelectItem>)}
              </SelectContent>
            </Select>
          </Card>
        </>
      )}

      {isProject && <LayoutImages lead={lead} onUpdate={onUpdate} canEdit={canEdit && !contract?.archived} />}

      {isProject && <ProposalAttachments lead={lead} onUpdate={onUpdate} canEdit={canEdit && !contract?.archived} />}

      {cost && <PreventivoCondizioni leadId={lead.id} canEdit={condizioniCanEdit} />}

    </div>
  );
}
